import {
  INTERNAL_DOMAINS,
  WORKSPACE_ENABLED_ROUTES,
  WORKSPACE_SLUG,
  MY_POINTS_WALLET_ROUTE,
  MY_FUNDS_URL,
  NEW_STORE_PATH_WITHOUT_WORKSPACE,
} from "@stadium/constants/routes";
import {
  SERVER_PHASE,
  REMOVE_TRAILING_SLASH_REGEX,
  DEFAULT_WORKSPACE_SLUG,
  MY_WORKSPACE_SLUG,
} from "@stadium/constants";
import { matchesUrl } from "@stadium/helpers/dom";

const MY_WORKSPACE_PATH_REGEX = new RegExp(
  String.raw`/w/${MY_WORKSPACE_SLUG}(?:/|(?=\?|#|$))`,
  "g",
);

const MY_WORKSPACE_PATH_PREFIX = `/w/${MY_WORKSPACE_SLUG}`;
const URL_BOUNDARY_REGEX = /[?#]/;
// Captures the trailing `/` or end-of-string so the boundary is preserved on replace.
const MY_WORKSPACE_CANONICALIZE_REGEX = new RegExp(String.raw`/w/${MY_WORKSPACE_SLUG}(/|$)`, "g");
const SWAG_DESIGN_WORKSPACE_PATH_REGEX = /\/w\/[^/]+\/design\/swag(?:\/|$)/;

// Rewrites `/w/my-space/...` to `/s/...` in the pathname only.
// Query strings/fragments are preserved as-is.
export const canonicalizeMySpacePath = (pathOrUrl) => {
  if (typeof pathOrUrl !== "string" || !pathOrUrl.includes(MY_WORKSPACE_PATH_PREFIX)) {
    return pathOrUrl;
  }
  const boundaryIdx = pathOrUrl.search(URL_BOUNDARY_REGEX);
  const pathnamePart = boundaryIdx === -1 ? pathOrUrl : pathOrUrl.slice(0, boundaryIdx);
  // Swag design routes are intentionally workspace-bound and must never be canonicalized to `/s/...`.
  // Keep it generic for any workspace slug: `/w/<workspaceSlug>/design/swag...`
  if (SWAG_DESIGN_WORKSPACE_PATH_REGEX.test(pathnamePart)) {
    return pathOrUrl;
  }
  // Prefix may appear only in query/hash (e.g. ?returnTo=/w/my-space/...); leave those untouched.
  if (!pathnamePart.includes(MY_WORKSPACE_PATH_PREFIX)) {
    return pathOrUrl;
  }
  const queryAndHash = boundaryIdx === -1 ? "" : pathOrUrl.slice(boundaryIdx);
  return pathnamePart.replace(MY_WORKSPACE_CANONICALIZE_REGEX, "/s$1") + queryAndHash;
};

const paramsToObject = (entries) => {
  const result = {};
  for (const [key, value] of entries) {
    // each 'entry' is a [key, value] tupple
    result[key] = value;
  }
  return result;
};

export const getParamsObject = (queryParamString) => {
  const urlParams = new URLSearchParams(queryParamString);
  const entries = urlParams.entries(); //returns an iterator of decoded [key,value] tuples
  const params = paramsToObject(entries); //{abc:"foo",def:"[asf]",xyz:"5"}
  return params;
};

export const extractSearchParams = (searchString = "") => {
  searchString = decodeURIComponent(searchString.replace(/^\?{1}/, ""));
  const params = searchString.length ? searchString.split("&") : [];

  let paramsObj = {};
  params.forEach((param) => {
    const pair = param.split("=");
    paramsObj = { ...paramsObj, [pair[0]]: pair[1] };
  });

  return paramsObj;
};

export const removeQueryParams = ({
  queryKeys = [],
  router,
  route = window.location.pathname,
  method = "replace",
  shallow,
} = {}) => {
  const currentSearchParams = window.location.search
    ? extractSearchParams(window.location.search)
    : {};

  queryKeys.forEach((queryKey) => delete currentSearchParams[queryKey]);

  router[method]?.(`${route}?${new URLSearchParams(currentSearchParams).toString()}`, undefined, {
    shallow,
  });
};

export const getUrlWithoutSearchParams = (url) => {
  const { origin, pathname } = new URL(url);
  return `${origin}${pathname}`;
};

export const removeTrailingSlash = (
  str,
  { homeRoute = "/", skipTrailingSlashForHome = false } = {},
) => {
  const replacedStr = str.replace(REMOVE_TRAILING_SLASH_REGEX, "");
  if (skipTrailingSlashForHome && str === homeRoute) {
    return str;
  }
  return replacedStr;
};

export const isSameDomain = ({ url = "" }) => {
  if (SERVER_PHASE) return false;
  const currentHost = new URL(window.location.href).hostname;
  const domainHost = new URL(url, window.origin).hostname;
  return currentHost === domainHost;
};

export const isInternalDomain = ({ url = "" }) => {
  if (SERVER_PHASE) return false;
  const { hostname } = new URL(url, window.origin);
  return isSameDomain({ url }) || INTERNAL_DOMAINS.includes(hostname);
};

export const getWorkspaceUrl = ({ url, workspaceSlug, pathname, prependWorkspaceSlug = true }) => {
  if (typeof url !== "string") {
    const { pathname: urlPathname } = url;
    const path = urlPathname.replace(window.location.origin, "");
    const workspaceFromUrl = pathname.split("/")[1];
    if (
      workspaceSlug === workspaceFromUrl &&
      matchesUrl({ url: path, pathname: WORKSPACE_ENABLED_ROUTES }) &&
      prependWorkspaceSlug
    ) {
      if (path.startsWith(`/${workspaceFromUrl}`)) {
        return { ...url, pathname: path };
      }
      return { ...url, pathname: `/${workspaceFromUrl}${path}` };
    }
    return url;
  }

  const path = url.replace(window.location.origin, "");
  const workspaceFromUrl = pathname.split("/")[1];
  if (
    workspaceSlug === workspaceFromUrl &&
    matchesUrl({ url: path, pathname: WORKSPACE_ENABLED_ROUTES }) &&
    prependWorkspaceSlug
  ) {
    const newPath = path.startsWith(`/${workspaceFromUrl}`) ? path : `/${workspaceFromUrl}${path}`;
    return newPath;
  }
  return url;
};

export const getRoutesWithWorkspace = (routes) =>
  routes
    .map((route) => {
      if (route.startsWith(WORKSPACE_SLUG) || route.includes("auth0")) return route;
      return `${WORKSPACE_SLUG}${route}`;
    })
    .filter(Boolean);

export const getPathnameFromUrl = (asPath) =>
  asPath ? asPath.split("?")[0].split("#")[0] : asPath;

export const getMyPointsWalletRoute = (flagEnabled) =>
  flagEnabled ? MY_POINTS_WALLET_ROUTE : MY_FUNDS_URL;

export const getLanguageFromPathname = (pathname) => {
  const language = pathname.match(/^\/([a-z]{2})\/([a-z]{2})\//)?.[2];
  return language;
};

export const getUrlWithWorkspaceSlug = ({
  url = "",
  fromWorkspaceSlug = DEFAULT_WORKSPACE_SLUG,
  toWorkspaceSlug,
}) => {
  if (!toWorkspaceSlug || typeof url !== "string") return url;

  const workspacePath = `/w/${fromWorkspaceSlug}`;
  if (url.includes(workspacePath)) {
    const updatedUrl = url.replaceAll(workspacePath, `/w/${toWorkspaceSlug}`);
    return toWorkspaceSlug === MY_WORKSPACE_SLUG
      ? updatedUrl.replaceAll(MY_WORKSPACE_PATH_REGEX, "/s/")
      : updatedUrl;
  }

  if (
    toWorkspaceSlug !== MY_WORKSPACE_SLUG &&
    url.includes("/s/") &&
    matchesUrl({ url, pathname: NEW_STORE_PATH_WITHOUT_WORKSPACE })
  ) {
    return url.replace("/s/", `/w/${toWorkspaceSlug}/`);
  }

  if (url.includes("/s/")) return url;

  const oldShopRouteWorkspaceSlug = `--${fromWorkspaceSlug}`;
  if (!url.includes(oldShopRouteWorkspaceSlug)) return url;

  return url.replace(oldShopRouteWorkspaceSlug, `--${toWorkspaceSlug}`);
};

export const getShopUrlWithCurrentWorkspaceSlug = (url, workspaceSlug) => {
  if (!workspaceSlug) return url;
  return getUrlWithWorkspaceSlug({
    url,
    fromWorkspaceSlug: DEFAULT_WORKSPACE_SLUG,
    toWorkspaceSlug: workspaceSlug,
  });
};
